/* =============================================================================
   DAILE Mono · DAILE Friday prices (2026-10-07). Loaded (defer) by snippets/mn-dfriday.liquid (part 'head') from
   layout/mono.liquid and layout/theme.liquid. Does nothing unless the inline head script found a DAILE Friday code
   (cookie `discount_code` / ?discount=, ^DF(20)?-[A-Z0-9]{8}$, before 2026-11-30 23:59:59 Riga) and set
   <html class="mn-df"> + window.mnDF.

   1. Prices: every [data-mn-price-cents="<cents>"] (Liquid, presentment currency) shows
      <s>€80</s> €56 — Math.round(cents × (100 − pct) / 100), pct 30 (DF-) or 20 (DF20-), formatted like daile-money (window.mnDF.fmt = the Liquid render of
      123456 → prefix / thousands / decimals / suffix of the visitor's currency). Gift card prices never carry the
      attribute. Prices changed later by other scripts (PDP set switch, chooser, drawer offers, search suggestions,
      cart re-render) are repainted by a MutationObserver; mn-pdp.js / mn.js keep the attribute in step.
   2. Bar (snippets/mn-dfriday.liquid part 'bar', under the announcement): × hides it for the session
      (sessionStorage 'mn-df-bar' → html.mn-df-nobar before first paint on the next pages).
   3. Code check (Storefront API) — also with an empty cart: is the code real and unused? Asked once per code and
      session with a throw-away validation cart that is never the visitor's cart (details at section 3 below).
      Not applicable (used / invalid) → off (as 4) + the `discount_code` cookie expired. Network / API error →
      the optimistic state stays (one console warning). While it runs nothing changes on screen.
   4. Reality check against the cart (/cart.js), after 3: Shopify's own numbers stay in the cart page / drawer
      (their line prices and totals are never repainted). Code not on the cart yet → added once per cart and session
      with /cart/update.js {discount} (other codes kept), so cart and drawer show Shopify's real discount. Cart with
      items but the code not applicable (used / invalid) → html.mn-df off, prices restored, bar hidden, hero back to
      the sign-up (mn.css), and sessionStorage 'mn-df-off' keeps it off for the session (no flash on the next pages).
   ============================================================================= */
(() => {
  'use strict';

  const w = window;
  const d = document;
  const h = d.documentElement;
  const cfg = w.mnDF;
  // Percent follows the code type: DF- (30%, sent until 2026-10-11) or DF20- (20%) — fill every [data-mn-df-pct]
  // inside body (<html> carries the same attribute as a flag; writing its textContent would wipe the page).
  const fillPct = () => d.querySelectorAll('body [data-mn-df-pct]').forEach((x) => { x.textContent = String(cfg && cfg.pct || 30); });
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', fillPct); else fillPct();
  if (!cfg || !cfg.code || !h.classList.contains('mn-df') || w.mnDFReady) return;
  w.mnDFReady = true;

  const code = String(cfg.code).toUpperCase();
  const SEL = '[data-mn-price-cents]';
  const ss = {
    get(k) { try { return w.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { w.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };
  let active = true;

  /* 1. Money format ========================================================== */

  const decode = (s) => {
    const t = d.createElement('textarea');
    t.innerHTML = String(s || '');
    return t.value.replace(/\s+$/, '').replace(/^\s+/, '');
  };
  const format = (() => {
    const s = decode(cfg.fmt);
    const first = s.search(/\d/);
    let last = -1;
    for (let i = s.length - 1; i >= 0; i -= 1) { if (/\d/.test(s[i])) { last = i; break; } }
    if (first > -1) {
      const core = s.slice(first, last + 1);
      const pre = s.slice(0, first);
      const post = s.slice(last + 1);
      let m = /^1(\D?)234(\D)56$/.exec(core);
      const dec = m ? m[2] : '';
      if (!m) m = /^1(\D?)235$/.exec(core);
      if (m) {
        const sep = m[1] || '';
        const group = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        return (cents) => {
          let num;
          if (dec) {
            const f = cents % 100;
            num = group(Math.floor(cents / 100)) + (f ? dec + String(f).padStart(2, '0') : '');
          } else {
            num = group(Math.round(cents / 100));
          }
          return pre + num + post;
        };
      }
    }
    // Unknown format: Intl in the visitor's currency, Latin digits (as daile-money / mn-com.js).
    return (cents) => {
      const S = w.Shopify || {};
      const cur = (S.currency && S.currency.active) || 'EUR';
      try {
        return new Intl.NumberFormat(S.locale || undefined, {
          style: 'currency', currency: cur, numberingSystem: 'latn',
          minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(2)} ${cur}`;
      }
    };
  })();

  /* 2. Prices ================================================================ */

  const label = (text) => {
    const l = d.createElement('span');
    l.className = 'mn-sr-only';
    l.textContent = `${text} `;
    return l;
  };

  // el.mnDf = { orig: innerHTML before, cents, html: innerHTML we wrote }
  const restore = (el) => {
    const st = el.mnDf;
    if (!st) return;
    if (el.innerHTML === st.html) el.innerHTML = st.orig;
    el.mnDf = null;
    el.classList.remove('mn-df-p');
  };

  const paint = (el) => {
    if (!active) { restore(el); return; }
    const cents = parseInt(el.getAttribute('data-mn-price-cents'), 10);
    const st = el.mnDf;
    const cur = el.innerHTML;
    const mine = !!st && cur === st.html;
    if (mine && st.cents === cents) return;
    const orig = mine ? st.orig : cur;
    if (!(cents > 0) || !orig.trim()) {
      if (mine) el.innerHTML = orig;
      el.mnDf = null;
      el.classList.remove('mn-df-p');
      return;
    }
    const was = d.createElement('s');
    was.className = 'mn-df-was';
    if (cfg.t && cfg.t.was) was.appendChild(label(cfg.t.was));
    was.insertAdjacentHTML('beforeend', orig);
    const now = d.createElement('span');
    now.className = 'mn-df-now';
    if (cfg.t && cfg.t.now) now.appendChild(label(cfg.t.now));
    now.appendChild(d.createTextNode(format(Math.round((cents * (100 - (cfg.pct || 30))) / 100))));
    el.textContent = '';
    el.append(was, ' ', now);
    el.classList.add('mn-df-p');
    el.mnDf = { orig, cents, html: el.innerHTML };
  };

  const paintAll = () => { d.querySelectorAll(SEL).forEach(paint); };

  /* 3. Code check (Storefront API) =========================================
     Shopify only says whether a code is real and unused through a cart, and the visitor's cart may be empty → a
     throw-away validation cart: cartCreate(1 × cheap available variant + the code) → discountCodes[].applicable.
     Endpoint / public token / variant: data attributes of this script's tag (snippets/mn-dfriday.liquid).
     Never the visitor's cart and no add-to-cart analytics:
     - credentials 'omit': no visitor cookies sent (so no cart / session of theirs), cookies in the answer ignored;
     - sent with the fetch of a blank same-origin iframe, never this window's: Shopify's web pixels / trekkie /
       apps wrap window.fetch (some before this script runs) and report Storefront API cart answers as "product
       added to cart"; they also read visitor tokens from the server-timing of this page's same-host requests
       (resource timing) — the iframe's requests are in its own timeline, never in this page's.
     Once per code and session: sessionStorage 'mn-df-chk:<CODE>' = 'ok|bad|err' + '|' + time (ok / bad 6 h,
     err 10 min). A stored 'ok' is asked again when the cart emptied since the last page (an order may have used
     the code). Answer: 'ok' · 'bad' (not applicable: used / invalid / expired) · 'err' (network / API trouble —
     the caller keeps the optimistic state) · '' (not configured). */

  const readJSON = (r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
  const same = (t) => String(t || '').trim().toUpperCase() === code;

  const CHK = `mn-df-chk:${code}`;
  const TTL = { ok: 6 * 3600e3, bad: 6 * 3600e3, err: 600e3 };
  const remember = (r) => ss.set(CHK, `${r}|${Date.now()}`);
  const recall = () => {
    const m = /^(ok|bad|err)\|(\d+)$/.exec(ss.get(CHK) || '');
    return m && Date.now() - Number(m[2]) < TTL[m[1]] ? m[1] : '';
  };
  const emptied = (() => {
    const prev = ss.get('mn-df-n');
    ss.set('mn-df-n', String(cfg.n || 0));
    return Number(prev) > 0 && !cfg.n;
  })();
  const sf = (() => {
    const s = d.currentScript || d.querySelector('script[data-mn-df-token]');
    const at = (n) => (s && s.getAttribute(n)) || '';
    return { api: at('data-mn-df-api'), token: at('data-mn-df-token'), variant: at('data-mn-df-variant') };
  })();
  const QUERY = 'mutation mnDfCheck($l: [CartLineInput!], $c: [String!]) { cartCreate(input: { lines: $l, discountCodes: $c }) '
    + '{ cart { discountCodes { code applicable } cost { totalAmount { amount } } lines(first: 1) { nodes { quantity } } } userErrors { field message } } }';

  // The browser's own fetch: the one of a blank same-origin iframe (no storefront script runs in it), removed after.
  const pristine = () => {
    let fr = null;
    try {
      fr = d.createElement('iframe');
      fr.hidden = true;
      fr.tabIndex = -1;
      fr.setAttribute('aria-hidden', 'true');
      fr.style.display = 'none';
      d.body.appendChild(fr);
      const cw = fr.contentWindow;
      if (cw && typeof cw.fetch === 'function') return { f: cw.fetch, win: cw, done() { fr.remove(); } };
    } catch (e) { /* fall through */ }
    if (fr) fr.remove();
    return null;
  };

  const verify = () => {
    const known = recall();
    if (known && !(known === 'ok' && emptied)) return Promise.resolve(known);
    const nat = sf.api && sf.token && sf.variant ? pristine() : null;
    if (!nat) {
      w.console.warn(`[mn-dfriday] code check skipped (${sf.api && sf.token && sf.variant ? 'no native fetch' : 'not configured'})`);
      return Promise.resolve('');
    }
    const AC = nat.win.AbortController;
    const ctl = typeof AC === 'function' ? new AC() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), 8000) : 0;
    return Promise.resolve().then(() => nat.f.call(nat.win, new URL(sf.api, w.location.href).href, {
      method: 'POST',
      credentials: 'omit',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Storefront-Access-Token': sf.token },
      body: JSON.stringify({ query: QUERY, variables: { l: [{ merchandiseId: sf.variant, quantity: 1 }], c: [code] } }),
      signal: ctl ? ctl.signal : undefined,
    })).then(readJSON).then((r) => {
      const cc = r && r.data && r.data.cartCreate;
      const cart = cc && cc.cart;
      const entry = cart && (cart.discountCodes || []).find((x) => same(x.code));
      const lines = (cart && cart.lines && cart.lines.nodes) || [];
      const errs = [].concat((r && r.errors) || [], (cc && cc.userErrors) || []);
      // The variant must be in the cart, or "not applicable" would only mean "empty cart".
      if (errs.length || !entry || !lines.length || typeof entry.applicable !== 'boolean') {
        throw new Error(errs.length ? errs.map((e) => e.message).join('; ') : 'unexpected answer');
      }
      return entry.applicable ? 'ok' : 'bad';
    }).catch((e) => {
      w.console.warn(`[mn-dfriday] code check failed, keeping the prices: ${(e && e.message) || e}`);
      return 'err';
    }).then((v) => {
      clearTimeout(timer);
      nat.done();
      remember(v);
      return v;
    });
  };

  // Shopify stores the code of /discount/<CODE> host-only: "discount_code=<CODE>; path=/; SameSite=Lax".
  const dropCookie = () => {
    const m = /(?:^|;\s*)discount_code=([^;]*)/.exec(d.cookie);
    if (!m) return;
    let v = m[1];
    try { v = decodeURIComponent(v.replace(/\+/g, ' ')); } catch (e) { /* keep raw */ }
    const all = v.split(/[\s,]+/).filter(Boolean);
    const rest = all.filter((x) => x.toUpperCase() !== code);
    if (rest.length === all.length) return; // the code came from ?discount= — the cookie holds something else
    d.cookie = rest.length
      ? `discount_code=${encodeURIComponent(rest.join(','))}; path=/; SameSite=Lax`
      : 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
  };

  /* 4. Cart check ============================================================ */

  const root = (() => {
    const r = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : `${r}/`;
  })();
  const getCart = () => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(readJSON);
  const setCodes = (codes) => fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') }),
  }).then(readJSON);

  // 'ok' applied · 'bad' items but not applicable · 'absent' not on the cart · 'wait' empty cart / gift cards only
  const judge = (c) => {
    const items = (c.items || []).filter((it) => !it.gift_card);
    const applied = (c.cart_level_discount_applications || []).some((a) => same(a.title))
      || items.some((it) => (it.line_level_discount_allocations || []).some((x) => x.discount_application && same(x.discount_application.title)));
    if (applied) return 'ok';
    const entry = (c.discount_codes || []).find((x) => same(x.code));
    if (entry && entry.applicable) return 'ok';
    if (!items.length) return entry ? 'wait' : 'absent-empty';
    return entry ? 'bad' : 'absent';
  };

  const bar = () => d.querySelector('[data-mn-df-bar]');
  // clear: the code itself is ruled out (3) → the cookie goes too, so it does not come back on the next visit.
  const off = (clear) => {
    if (clear) dropCookie();
    if (!active) return;
    active = false;
    ss.set('mn-df-off', code);
    if (mo) mo.disconnect();
    const b = bar();
    if (b && b.contains(d.activeElement)) d.activeElement.blur();
    h.classList.remove('mn-df');
    h.removeAttribute('data-mn-df');
    d.querySelectorAll('.mn-df-p').forEach(restore);
  };

  let checking = false;
  let again = false;
  const check = () => {
    if (!active) return;
    if (checking) { again = true; return; }
    checking = true;
    getCart().then((c) => {
      let v = judge(c);
      const syncKey = `${code}|${c.token || ''}`;
      if ((v === 'absent' || v === 'absent-empty') && ss.get('mn-df-sync') !== syncKey) {
        // Put the code on the cart once per cart and session (the e-mail link normally does this already).
        ss.set('mn-df-sync', syncKey);
        const prev = (c.discount_codes || []).map((x) => x.code).filter(Boolean);
        return setCodes(prev.concat(code)).then((c2) => {
          v = judge(c2);
          // Shopify refused it (used / invalid): leave the cart's codes as they were.
          if (v === 'bad') setCodes(prev).catch(() => {});
          if (v === 'ok' && (c2.items || []).length) {
            // Cart page: Liquid totals were rendered without the code → reload once; drawer: re-render.
            if (d.body.classList.contains('mn-template-cart')) { w.location.reload(); return 'ok'; }
            if (w.DAILE_CART && typeof w.DAILE_CART.refresh === 'function') w.DAILE_CART.refresh().catch(() => {});
          }
          return v;
        });
      }
      return v;
    }).then((v) => {
      if (v === 'ok') { ss.set('mn-df-ok', code); remember('ok'); }
      else if (v === 'bad' || v === 'absent') off();
    }).catch(() => { /* offline / blocked: keep the optimistic state */ }).then(() => {
      checking = false;
      if (again) { again = false; check(); }
    });
  };
  let timer = 0;
  const checkSoon = () => { clearTimeout(timer); timer = setTimeout(check, 600); };

  /* 5. Observer: repaint prices other scripts changed, re-check after cart changes. */

  const mo = 'MutationObserver' in w ? new MutationObserver((records) => {
    if (!active) return;
    const todo = new Set();
    let cart = false;
    records.forEach((r) => {
      const t = r.target.nodeType === 1 ? r.target : r.target.parentElement;
      if (!t) return;
      const host = t.closest(SEL);
      if (host) todo.add(host);
      if (r.type === 'childList') {
        r.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches(SEL)) todo.add(n);
          n.querySelectorAll(SEL).forEach((e) => todo.add(e));
        });
      }
      if (!cart && t.closest('cart-count')) cart = true;
    });
    if (todo.size) {
      todo.forEach(paint);
      mo.takeRecords(); // our own writes
    }
    if (cart) checkSoon();
  }) : null;

  /* 6. Start ================================================================= */

  const start = () => {
    paintAll();
    if (mo) mo.observe(d.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-mn-price-cents'] });

    d.addEventListener('click', (e) => {
      const x = e.target.closest('[data-mn-df-close]');
      if (!x) return;
      ss.set('mn-df-bar', code);
      h.classList.add('mn-df-nobar');
      const next = d.querySelector('.mn-header a[href], .mn-header button');
      if (next) next.focus({ preventScroll: true });
    });

    // Liquid already saw the code applied → valid, nothing to ask. Otherwise the code check (3) first — prices stay
    // as painted while it runs — then the cart check (4), unless the cart is empty and the code already synced.
    const a = String(cfg.a || '');
    const seen = a.indexOf(`|${code}|`) > -1;
    if (seen) {
      ss.set('mn-df-ok', code);
      remember('ok');
    } else {
      const synced = cfg.n === 0 && String(ss.get('mn-df-sync') || '').indexOf(`${code}|`) === 0;
      verify().then((v) => {
        if (v === 'bad') off(true);
        else if (!synced) check();
      });
    }

    // Back / forward cache: the cart may have changed on another page.
    w.addEventListener('pageshow', (e) => { if (e.persisted) check(); });
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
